import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Alert,
  Divider,
  Checkbox,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ProviderNegotiation, NegotiationCondition, NegotiationConditionStatus } from '~/types/coordinate';

interface Props {
  negotiation: ProviderNegotiation;
  onUpdateCondition: (conditionId: string, updates: Partial<NegotiationCondition>) => void;
  onAccept: () => void;
}

const ConditionRow: React.FC<{
  condition: NegotiationCondition;
  onUpdate: (updates: Partial<NegotiationCondition>) => void;
}> = ({ condition, onUpdate }) => {
  const isMet = condition.status === 'met';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.75 }}>
      <Checkbox
        size="small"
        checked={isMet}
        onChange={() => onUpdate({ status: isMet ? 'pending' : 'met' })}
        sx={{ mt: -0.25, p: 0.5 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.82rem',
            textDecoration: isMet ? 'line-through' : 'none',
            color: isMet ? 'text.disabled' : 'text.primary',
          }}
        >
          {condition.description}
        </Typography>
        {condition.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
            {condition.notes}
          </Typography>
        )}
        {!isMet && condition.status === 'blocked' && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
            <Chip
              label="Blocked"
              size="small"
              sx={{ height: 17, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#ffebee', color: '#b71c1c' }}
            />
            <Button
              size="small"
              sx={{ fontSize: '0.7rem', py: 0, minWidth: 0 }}
              onClick={() => onUpdate({ status: 'pending' })}
            >
              Reopen
            </Button>
          </Stack>
        )}
        {!isMet && condition.status === 'pending' && (
          <Button
            size="small"
            color="error"
            sx={{ fontSize: '0.7rem', py: 0, minWidth: 0, mt: 0.25 }}
            onClick={() => onUpdate({ status: 'blocked' })}
          >
            Flag as blocked
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ProviderNegotiationThread: React.FC<Props> = ({ negotiation, onUpdateCondition, onAccept }) => {
  const allMet = negotiation.conditions.every(c => c.status === 'met');
  const anyBlocked = negotiation.conditions.some(c => c.status === 'blocked');
  const pendingCount = negotiation.conditions.filter(c => c.status === 'pending').length;
  const isResolved = !!negotiation.resolvedAt;

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Provider Negotiation</Typography>

      <Box
        sx={{
          p: 1.5, mb: 2,
          borderRadius: 1,
          bgcolor: '#f0eef8',
          border: '1px solid rgba(99,95,153,0.2)',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.65rem' }}>
          {negotiation.providerName}
        </Typography>
        {negotiation.providerMessage && (
          <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.82rem', color: '#2e2d4e' }}>
            "{negotiation.providerMessage}"
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
          Started {new Date(negotiation.startedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.65rem', display: 'block', mb: 1 }}>
        Conditions ({negotiation.conditions.filter(c => c.status === 'met').length}/{negotiation.conditions.length} met)
      </Typography>

      {negotiation.conditions.map((cond, i) => (
        <React.Fragment key={cond.id}>
          <ConditionRow
            condition={cond}
            onUpdate={updates => onUpdateCondition(cond.id, updates)}
          />
          {i < negotiation.conditions.length - 1 && <Divider sx={{ opacity: 0.4 }} />}
        </React.Fragment>
      ))}

      <Box sx={{ mt: 2 }}>
        {anyBlocked && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="body2" fontSize="0.8rem">
              One or more conditions are blocked — resolve or select a different provider.
            </Typography>
          </Alert>
        )}
        {allMet && !isResolved && (
          <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="body2" fontSize="0.8rem">
              All conditions met. Accept to confirm the placement.
            </Typography>
          </Alert>
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="contained"
            disabled={!allMet || isResolved}
            onClick={onAccept}
            fullWidth
            sx={{ bgcolor: '#00bda1', '&:hover': { bgcolor: '#009e87' }, '&.Mui-disabled': { bgcolor: 'rgba(0,189,161,0.3)' } }}
          >
            {isResolved
              ? <Stack direction="row" alignItems="center" spacing={0.75}>
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                  <span>Placement Accepted</span>
                </Stack>
              : `Accept Placement with ${negotiation.providerName}`}
          </Button>
          <Tooltip
            title="Accepting confirms the placement with the provider. In production, this sends a structured confirmation through the platform (or generates a draft message for your call if they're not on the platform). The event is logged to the activity feed and the case moves to Accepted."
            placement="top"
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
          </Tooltip>
        </Stack>

        {!allMet && !anyBlocked && pendingCount > 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75, textAlign: 'center', fontSize: '0.72rem' }}>
            {pendingCount} condition{pendingCount !== 1 ? 's' : ''} still pending
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ProviderNegotiationThread;
